import {useCallback, useEffect, useMemo, useState} from 'react'
import debounce from 'lodash/debounce.js'

import {isPositiveNumeric} from '../../utils.js'
import {splitOz, combineToOz, lbToOz} from '../../weight.js'
import NumberInput from './NumberInput.js'
import ScaleControl from './ScaleControl.js'
import className from '../../className.js'

function calculateTotalWeightOz(lbStr, ozStr, setSpecialWeight) {
  const lb = Number(lbStr)
  let oz = Number(ozStr)

  // if user inputs oz greater than 16 then take only the oz value
  if (oz >= 16) {
    setSpecialWeight(oz)

    return oz
  }

  // if user inputs lb with a decimal then take only the lb value
  if (lbStr.match(/\./)) {
    oz = lbToOz(lb)

    setSpecialWeight(oz)

    return oz
  }

  // otherwise combine the both lb and oz
  return combineToOz(lb, oz)
}

function rawOnInputChange(
  lb,
  oz,
  lbError,
  ozError,
  weightOz,
  setWeightOz,
  disabled,
  setSpecialWeight,
) {
  if (disabled || weightOz === null || lbError || ozError) {
    return
  }

  const newWeightOz = calculateTotalWeightOz(lb, oz, setSpecialWeight)

  if (newWeightOz !== weightOz) {
    setWeightOz(newWeightOz)
  }
}

function getStateFromTotalOz(totalOz, setLb, setOz) {
  // copy input oz to the local state values

  if (totalOz === null) {
    setLb('')
    setOz('')
  }

  const {lb, oz} = splitOz(totalOz)

  setLb(`${lb}`)
  setOz(`${oz}`)
}

/**
 * Input weight in separate lb and oz fields with validation and parsing
 *
 * @param {Object} props - Component props
 * @param {Number} [props.weightOz]
 * @param {Function} props.setWeightOz
 * @param {String} [props.idLb]
 * @param {String} [props.metaClassName]
 * @param {Boolean} [props.allowZero]
 * @param {Boolean} [props.showScaleControl]
 * @param {Boolean} [props.autoFocus]
 * @param {Boolean} [props.disabled]
 * @param {Number} [props.delay]
 * @param {Function} [props.onEnterKeyPress]
 * @param {Function} [props.onErrorChange]
 * @param {Number} [props.widthPerCharacter]
 *
 * @returns {React.ReactNode} - Rendered component
 */
export default function WeightInput({
  idLb,
  weightOz,
  setWeightOz,
  metaClassName,
  allowZero = true,
  showScaleControl,
  autoFocus,
  disabled,
  delay = 500,
  onEnterKeyPress,
  onErrorChange,
  widthPerCharacter,
}) {
  const [lb, setLb] = useState('')
  const [oz, setOz] = useState('')
  const [specialWeight, setSpecialWeight] = useState(null)

  const lbError = useMemo(() => lb !== '' && !isPositiveNumeric(lb), [lb])
  const ozError = useMemo(() => oz !== '' && !isPositiveNumeric(oz), [oz])
  const invalidWeight = useMemo(
    () =>
      weightOz !== null ? (allowZero ? weightOz < 0 : weightOz <= 0) : false,
    [weightOz, allowZero],
  )

  const onInputChange = useMemo(
    () => debounce(rawOnInputChange, delay),
    [delay],
  )

  const localOnEnterKeyPress = useCallback(() => {
    onInputChange(
      lb,
      oz,
      lbError,
      ozError,
      weightOz,
      setWeightOz,
      disabled,
      setSpecialWeight,
    )

    // make it happen right now
    onInputChange.flush()

    if (onEnterKeyPress) {
      onEnterKeyPress()
    }
  }, [
    lb,
    oz,
    lbError,
    ozError,
    weightOz,
    setWeightOz,
    disabled,
    onInputChange,
    onEnterKeyPress,
  ])

  const onIncrementOz = useCallback((lb, oz) => {
    const newTotalOz = combineToOz(lb, oz)

    if (newTotalOz < 0) {
      return
    }

    getStateFromTotalOz(newTotalOz, setLb, setOz)
  }, [])

  useEffect(() => {
    getStateFromTotalOz(weightOz, setLb, setOz)
  }, [weightOz])

  useEffect(() => {
    onInputChange(
      lb,
      oz,
      lbError,
      ozError,
      weightOz,
      setWeightOz,
      disabled,
      setSpecialWeight,
    )
  }, [lb, oz])

  useEffect(() => {
    if (specialWeight === null) {
      return
    }

    // something set specialWeight, so take it and calculate lb and oz

    setSpecialWeight(null)

    getStateFromTotalOz(specialWeight, setLb, setOz)
  }, [specialWeight])

  useEffect(() => {
    if (onErrorChange) {
      onErrorChange({
        lb: lbError,
        oz: ozError,
      })
    }
  }, [lbError, ozError])

  return (
    <div
      className={className`meta-input-weightOz ${metaClassName} ${{
        error: invalidWeight,
      }}`}
    >
      <div className="flex flex-wrap">
        <div className="flex wrap--weight-input margin-bottom-7 margin-right-3">
          <NumberInput
            id={idLb}
            value={lb}
            isInvalid={invalidWeight || lbError}
            onChange={(lb) => setLb(lb)}
            onIncrement={(lb) => setLb(`${lb}`)}
            onDecrement={(lb) => setLb(`${lb}`)}
            min={0}
            onEnterKeyPress={() => localOnEnterKeyPress()}
            autoFocus={autoFocus}
            disabled={disabled}
            widthPerCharacter={widthPerCharacter}
          />
          <span
            className={className`input__unit inline-block ${{
              'op-50': disabled,
            }}`}
          >
            lb
          </span>
        </div>
        <div className="flex wrap--weight-input margin-bottom-7 margin-right-3">
          <NumberInput
            value={oz}
            isInvalid={invalidWeight || ozError}
            disabled={disabled}
            onChange={(oz) => setOz(oz)}
            onIncrement={(oz) => onIncrementOz(lb, oz)}
            onDecrement={(oz) => onIncrementOz(lb, oz)}
            onEnterKeyPress={() => localOnEnterKeyPress()}
            widthPerCharacter={widthPerCharacter}
          />
          <span
            className={className`input__unit inline-block ${{
              'op-50': disabled,
            }}`}
          >
            oz
          </span>
        </div>
        {!disabled && showScaleControl && (
          <ScaleControl
            onChange={(scaleWeight) =>
              getStateFromTotalOz(scaleWeight, setLb, setOz)
            }
          />
        )}
      </div>
    </div>
  )
}
